import * as React from "react";
import { Transition } from "@headlessui/react";
import { Fragment } from "react";
import { cn } from "@utils/cn";
import ReactDOM from "react-dom";
import { usePortalRoot } from "../../contexts/PortalRootContext";
import { useTooltipPosition } from "../../hooks/useTooltipPosition";

interface TooltipProviderProps {
  children: React.ReactNode;
}

interface TooltipProps {
  children: React.ReactNode; // Should contain a trigger and content
  portalRoot?: HTMLElement | null; // Add portalRoot prop
}

interface TooltipTriggerProps {
  children: React.ReactNode;
}

interface TooltipContentProps {
  children: React.ReactNode;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  alignOffset?: number;
}

// TooltipProvider is a no-op for this headlessui-based implementation.
const TooltipProvider: React.FC<TooltipProviderProps> = ({ children }) => {
  return <>{children}</>;
};

const Tooltip: React.FC<TooltipProps> = ({ children, portalRoot: propPortalRoot }) => {
  const { portalRoot: contextPortalRoot } = usePortalRoot();
  const portalRoot = propPortalRoot || contextPortalRoot;

  const [isOpen, setIsOpen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);

  // Find the trigger and content children
  const childrenArray = React.Children.toArray(children);
  const triggerChild = childrenArray.find(
    (child) => React.isValidElement(child) && (child.type as any).displayName === "TooltipTrigger"
  );
  const contentChild = childrenArray.find(
    (child) => React.isValidElement(child) && (child.type as any).displayName === "TooltipContent"
  );

  if (!triggerChild || !contentChild) {
    console.warn("Tooltip requires both TooltipTrigger and TooltipContent children.");
    return null;
  }

  // Extract props from TooltipContent for positioning and styling
  const { 
    className, 
    side = "top", 
    align = "center", 
    sideOffset = 8, 
    alignOffset = 0,
    ...contentProps 
  } = (contentChild as React.ReactElement<TooltipContentProps>).props;

  // Get tooltip content as string for dimension estimation
  const contentText = React.useMemo(() => {
    const contentElement = (contentChild as React.ReactElement).props.children;
    if (typeof contentElement === "string") {
      return contentElement;
    }
    // For complex content, we'll let the hook handle it with actual measurements
    return "";
  }, [contentChild]);

  // Use the robust positioning hook
  const { tooltipStyle, tooltipRef } = useTooltipPosition(triggerRef, {
    side,
    align,
    sideOffset,
    alignOffset,
    portalRoot,
    isOpen,
    content: contentText,
  });

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(false);
  };

  // Fallback positioning classes for when no portal is used
  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  // Get the actual trigger element from TooltipTrigger children
  const triggerElement = (triggerChild as React.ReactElement).props.children;

  const tooltipContent = (
    <Transition
      as={Fragment}
      show={isOpen}
      enter="transition-opacity duration-150"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        ref={tooltipRef}
        className={cn(
          "overflow-hidden rounded-md border bg-white px-3 py-1.5 text-sm text-gray-900 shadow-md",
          "min-w-xs max-w-xl whitespace-pre-wrap", // Allow text wrapping
          portalRoot ? "absolute" : positionClasses[side], // Use dynamic positioning with portal, static without
          className
        )}
        style={portalRoot ? tooltipStyle : {}}
        onMouseEnter={handleMouseEnter} // Keep open if mouse moves to content
        onMouseLeave={handleMouseLeave}
        {...contentProps}
      >
        {(contentChild as React.ReactElement).props.children}
      </div>
    </Transition>
  );

  return (
    <div className="relative">
      {/* Wrap the trigger element with event handlers */}
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        className="w-full"
      >
        {triggerElement}
      </div>

      {portalRoot ? ReactDOM.createPortal(tooltipContent, portalRoot) : tooltipContent}
    </div>
  );
};

// TooltipTrigger and TooltipContent are now just wrappers for their children
// and are used to identify the parts of the Tooltip.
const TooltipTrigger = React.forwardRef<
  HTMLElement,
  TooltipTriggerProps
>(({ children }, ref) => {
  // Just return the children as-is, the parent Tooltip will handle cloning
  return React.cloneElement(children as React.ReactElement, { ref });
});
TooltipTrigger.displayName = "TooltipTrigger";

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  TooltipContentProps
>(({ children, ...props }, ref) => {
  // The actual panel will be rendered by the parent Tooltip component
  return React.cloneElement(children as React.ReactElement, { ref, ...props });
});
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
